import React, { useEffect, useState } from 'react';
import { Spin, Typography, Row, Col, message, Empty, Pagination } from 'antd';
import OrderHistoryApiService from '../services/OrderHistoryApiService';
import '../styles/OrderHistory.css';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const statusColor = {
  completed: 'green',
  cancelled: 'orange',
  failed: 'red',
  pending: 'blue',
};

const statusText = {
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
  pending: 'Pending',
};

const formatCurrency = (amount, currency = 'đ') => {
  if (currency === 'USD' || currency === '$') {
    return `$${amount?.toLocaleString('en-US')}`;
  }
  return amount?.toLocaleString('vi-VN') + 'đ';
};

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const res = await OrderHistoryApiService.getMyOrders({ page: currentPage, limit: pageSize });
        const orders = res.data?.data?.orders || [];
        setOrders(orders);
        setTotal(res.data?.data?.pagination?.totalItems || 0);
        console.log('Fetched orders:', orders);
      } catch (err) {
        setError('Failed to load order history.');
        message.error('Failed to load order history.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [currentPage]);

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  if (loading) {
    return <div className="order-history-spinner"><Spin size="large" /></div>;
  }

  if (error) {
    return <div className="order-history-error">{error}</div>;
  }

  return (
    <div className="order-history-container">
      <h1 className="order-history-title">Orders History</h1>
      {orders.length === 0 ? (
        <Empty description="No orders found." style={{ marginTop: 48 }} />
      ) : (
        <>
        <div className="order-history-list">
          {orders.map(order => {
            const isExpanded = expandedOrders[order._id];
            const coursesToShow = isExpanded ? order.courseIds : order.courseIds?.slice(0, 2);
            const hasMore = order.courseIds && order.courseIds.length > 2;
            return (
              <div className="order-history-card" key={order._id}>
                <div className="order-history-header">
                  <span className="order-history-code">Order code: {order.shortCode || order._id}</span>
                  <span className="order-history-status">
                    <span style={{ color: '#222' }}>Status:</span>{' '}
                    <span style={{ color: statusColor[order.status?.toLowerCase()] || '#333', fontWeight: 600 }}>
                      {statusText[order.status?.toLowerCase()] || order.status}
                    </span>
                  </span>
                </div>
                <div className="order-history-courses">
                  <span className="order-history-total-courses">Total Courses: {order.courseIds?.length || 0} course{order.courseIds?.length === 1 ? '' : 's'}</span>
                  {coursesToShow && coursesToShow.map((course, idx) => {
                    const isCompleted = order.status?.toLowerCase() === 'completed';
                    const handleClick = () => {
                      if (isCompleted) {
                        navigate(`/learner/my-courses/${course.slug}`);
                      } else {
                        navigate(`/courses/${course.slug}`);
                      }
                    };
                    return (
                      <div
                        className="order-history-course-card"
                        key={course._id || idx}
                        style={{ cursor: 'pointer' }}
                        onClick={handleClick}
                        title={isCompleted ? 'Go to learning page' : 'View course details'}
                      >
                        <img className="order-history-course-img" src={course.imageUrl || '/imgs/banner1.png'} alt={course.name} />
                        <div className="order-history-course-info">
                          <div className="order-history-course-title">{course.name}</div>
                          <div className="order-history-course-category">{course.category?.name}</div>
                          <div className="order-history-course-price">{formatCurrency(course.price, '$')}</div>
                        </div>
                      </div>
                    );
                  })}
                  {hasMore && (
                    <button
                      className={`order-history-show-more-btn${isExpanded ? ' expanded' : ''}`}
                      onClick={() => toggleExpand(order._id)}
                    >
                      <span>{isExpanded ? 'Show less' : `Show more`}</span>
                      <span className="arrow">▼</span>
                    </button>
                  )}
                </div>
                <div className="order-history-footer">
                  <span className="order-history-total-amount">
                    Total Amount By USD: <span style={{ color: '#52c41a', fontWeight: 700 }}>
                      {formatCurrency(order.courseIds?.reduce((sum, c) => sum + (c.price || 0), 0), '$')}
                    </span>
                    <br/>
                    Total Amount By VND: <span style={{ color: '#e74c3c', fontWeight: 700 }}>{formatCurrency(order.totalAmount, 'đ')}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
        </>
      )}
    </div>
  );
};

export default OrderHistory; 